import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  GoogleAuthProvider,
  connectAuthEmulator 
} from 'firebase/auth';
import { 
  getFirestore, 
  connectFirestoreEmulator 
} from 'firebase/firestore';
import { 
  getFunctions, 
  connectFunctionsEmulator 
} from 'firebase/functions';
import { 
  initializeAppCheck, 
  ReCaptchaEnterpriseProvider,
  ReCaptchaV3Provider,
  getToken 
} from 'firebase/app-check';

// Firebase configuration
// These values are safe to expose in client-side code
const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
const appId = import.meta.env.VITE_FIREBASE_APP_ID;

// Validate required environment variables
if (!apiKey || apiKey === 'YOUR_API_KEY' || !appId || appId === 'YOUR_APP_ID') {
  console.warn('⚠️ Firebase API Key ou App ID não configurados!');
  console.warn('   Crie um arquivo app/.env.local com:');
  console.warn('   VITE_FIREBASE_API_KEY=sua_api_key');
  console.warn('   VITE_FIREBASE_APP_ID=seu_app_id');
  console.warn('   Veja README.md para mais detalhes.');
}

const firebaseConfig = {
  apiKey: apiKey || 'YOUR_API_KEY',
  authDomain: 'autodata-908af.firebaseapp.com',
  projectId: 'autodata-908af',
  storageBucket: 'autodata-908af.firebasestorage.app',
  messagingSenderId: '43638268395',
  appId: appId || 'YOUR_APP_ID',
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'southamerica-east1');

// Google Auth Provider
export const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');

// Connect to emulators in development
// Set VITE_USE_EMULATORS=true in .env to use local emulators
const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';

if (useEmulators) {
  // Use the same hostname as the current page to avoid CORS issues
  // This allows testing from different devices on the network
  const emulatorHost = window.location.hostname;
  
  console.log(`🔧 Connecting to Firebase emulators on ${emulatorHost}...`);
  connectAuthEmulator(auth, `http://${emulatorHost}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, emulatorHost, 8080);
  connectFunctionsEmulator(functions, emulatorHost, 5001);
  console.log(`✅ Connected to emulators: Auth(9099), Firestore(8080), Functions(5001)`);
  
  // App Check: Use debug token in development
  // Get debug token from: https://console.firebase.google.com/project/_/appcheck/apps
  if (typeof window !== 'undefined') {
    (window as any).FIREBASE_APPCHECK_DEBUG_TOKEN = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN || true;
  }
} else {
  console.log('🌐 Using production Firebase services');
}

// Initialize App Check for security
// Protects against abuse and unauthorized access
// Only initialize if we have valid Firebase config
const hasValidConfig = apiKey && apiKey !== 'YOUR_API_KEY' && appId && appId !== 'YOUR_APP_ID';

if (typeof window !== 'undefined' && hasValidConfig) {
  try {
    const reCaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
    
    if (useEmulators) {
      // In development with emulators, use debug provider
      // Debug token is already set above when useEmulators is true
      const appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider('00000000-0000-0000-0000-000000000000'), // Debug provider
        isTokenAutoRefreshEnabled: true,
      });
      console.log('🔧 App Check initialized in debug mode (emulators)');
    } else if (reCaptchaSiteKey) {
      // Use reCAPTCHA Enterprise in production
      const appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(reCaptchaSiteKey),
        isTokenAutoRefreshEnabled: true, // Auto-refresh tokens
      });
      
      console.log('✅ App Check initialized with reCAPTCHA Enterprise');
      
      // Get initial token to verify it's working
      getToken(appCheck, false)
        .then((token) => {
          console.log('✅ App Check token obtained successfully');
        })
        .catch((error) => {
          console.warn('⚠️ App Check token error (non-critical):', error);
        });
    } else {
      console.warn('⚠️ App Check not initialized: VITE_RECAPTCHA_SITE_KEY not set');
      console.warn('   Configure App Check in Firebase Console and add VITE_RECAPTCHA_SITE_KEY to .env');
    }
  } catch (error) {
    console.error('❌ Error initializing App Check:', error);
    // Don't block app if App Check fails to initialize
  }
} else if (typeof window !== 'undefined' && !hasValidConfig) {
  console.warn('⚠️ App Check não inicializado: Firebase config inválido');
  console.warn('   Configure VITE_FIREBASE_API_KEY e VITE_FIREBASE_APP_ID no arquivo .env.local');
}

export default app;

